import React from 'react';
import { Tooltip } from 'antd';
import { Eye } from '@phosphor-icons/react';
import { formatCurrency } from 'utils';
import { ProductData, ProductItemProps } from 'types';
import HorizontalScrollContainer from '../common/ScrollContainer/HorizontalScrollContainer';

const ProductItem: React.FC<ProductItemProps> = ({
  id,
  name = '---',
  code,
  imageUrl,
  unitPrice,
  category,
  quantity,
  actualQuantity = undefined,
  unit = '---',
  brand = '---',
  currencyUnit,
  onSelectProduct,
  isSelected = false,
  isQuickView = false,
  isPOS = false,
  dimension = '---',
  attribute = '---',
  atrtributePrice = 0,
  isDisplayTotal = true,
  isDisplaySubtotal = true,
  reason,
}) => {
  // Create a product object to pass to onSelectProduct
  const handleItemClick = () => {
    if (onSelectProduct) {
      const product: ProductData = {
        id,
        name,
        imageUrl,
        unitPrice,
        category: category || '---',
        quantity,
        currencyUnit,
        actualQuantity,
        unit,
        reason,
      };
      onSelectProduct(product);
    }
  };

  // Calculate difference between expected and actual quantity
  const difference = actualQuantity !== null && actualQuantity !== undefined ? actualQuantity - quantity : undefined;
  // Calculate subtotal based on expected quantity
  const subtotal = (Number(unitPrice) + Number(atrtributePrice)) * (Number(actualQuantity) || Number(quantity));

  // Calculate total based on actual quantity if available, otherwise use subtotal
  const total = actualQuantity !== null && actualQuantity !== undefined ? unitPrice * actualQuantity : undefined;
  // Format difference with + or - sign
  const formattedDifference =
    difference !== undefined ? (difference > 0 ? `+${difference}` : difference.toString()) : '';
  return (
    <div
      className={`w-full bg-white rounded-2xl p-3 my-1 border cursor-pointer transition-all
      ${
        isSelected
          ? 'border-[var(--primaryColor500)]'
          : 'border-[var(--textBlack12)] hover:border-[var(--primaryColor300)]'
      }`}
      style={
        isSelected
          ? {
              boxShadow: '0 0 0 1px var(--primaryColor100)',
              filter: 'drop-shadow(0 0 1px var(--primaryColor300))',
            }
          : {}
      }
      onClick={handleItemClick}
    >
      <HorizontalScrollContainer className='w-full' contentClassName='h-auto' showArrows={true}>
        <div className='flex items-center gap-3'>
          {/* Product image and basic info  */}
          <div className='flex gap-3 items-center flex-shrink-0 min-w-[280px] w-[400px]'>
            <div className='w-[60px] h-[60px] bg-[var(--textBlack12)] rounded-lg'>
              {imageUrl && <img src={imageUrl} alt={name} className='w-full h-full object-cover rounded-lg' />}
            </div>

            <div className={'flex flex-col justify-between h-full min-w-[200px]' + (code ? ' gap-1' : ' gap-[11px]')}>
              <Tooltip title={name} placement='topLeft'>
                <div className='font-bold text-[18px] leading-[18px] text-[var(--textBlack)] truncate'>{name}</div>
              </Tooltip>

              {code && (
                <div className='flex items-center gap-[4px]'>
                  <span className='text-[13px] text-[var(--textBlack56)]'>Mã:</span>
                  <span className='text-[13px] text-[var(--textBlack56)]'>{code || '---'}</span>
                </div>
              )}
              {(attribute || unit) && (
                <div className='flex items-center gap-[11px]'>
                  {unit && <span className='text-[14px] text-[var(--textBlack56)] italic'>({unit})</span>}
                  {attribute && <span className='text-[14px] text-[var(--textBlack56)] italic'>{attribute}</span>}
                </div>
              )}
              <div className='flex items-center gap-[11px]'>
                <span className='text-[14px] text-[var(--textBlack56)]'>Đơn giá:</span>
                <span className='text-[14px] font-bold text-[var(--primaryColor)]'>
                  {formatCurrency(unitPrice)} {currencyUnit}
                </span>
              </div>
            </div>
          </div>

          {/* Category  */}
          {!isPOS && (
            <>
              <div className='flex flex-col justify-between h-full gap-[11px] flex-shrink-0 w-[120px]'>
                <div className='text-[14px] text-[var(--textBlack56)]'>Thương hiệu</div>
                <div className='text-[14px] font-medium text-[var(--primaryColor)] truncate'>{brand}</div>
              </div>
              <div className='flex flex-col justify-between h-full gap-[11px] flex-shrink-0 w-[120px]'>
                <div className='text-[14px] text-[var(--textBlack56)]'>Danh mục</div>
                <div className='text-[14px] font-medium text-[var(--primaryColor)] truncate'>{category}</div>
              </div>

              {/* Expected quantity  */}
              <div className='flex flex-col justify-center h-full items-center gap-[11px] flex-shrink-0 w-[80px]'>
                <div className='text-[14px] text-[var(--textBlack56)]'>Số lượng</div>
                <div className='rounded-md text-center'>
                  <div className='text-[12px] font-medium text-[var(--primaryColor)]'>
                    {difference !== undefined && isQuickView ? actualQuantity : quantity}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Actual quantity input  */}
          {!isQuickView && (
            <div className='flex flex-col justify-between h-full min-h-[41px] gap-[11px] items-center flex-shrink-0 w-[80px]'>
              <div className='text-[14px] text-[var(--textBlack56)]'>SL thực tế</div>
              <div className='rounded-md text-center'>
                <div className='text-[12px] font-medium text-[var(--primaryColor)]'>{actualQuantity}</div>
              </div>
            </div>
          )}

          {/* Difference  */}
          {!isQuickView && (
            <div className='flex flex-col justify-between h-full min-h-[41px] items-center gap-[11px] flex-shrink-0 w-[80px]'>
              <div className='text-[14px] text-[var(--textBlack56)]'>Chênh lệch</div>
              <div
                className='text-center'
                style={{
                  color:
                    difference !== undefined
                      ? difference < 0
                        ? 'var(--sematicErrorColor500)'
                        : difference > 0
                          ? 'var(--sematicWarningColor)'
                          : difference === 0
                            ? 'var(--sematicSuccessColor500)'
                            : 'inherit'
                      : 'inherit',
                }}
              >
                {formattedDifference}
              </div>
            </div>
          )}

          {/* Unit  */}
          {/* <div className='flex flex-col justify-between h-full gap-[11px] flex-shrink-0 w-[120px]'>
            <div className='text-[14px] text-[var(--textBlack56)]'>Đơn vị tính</div>
            <div className='text-[14px] font-medium text-[var(--primaryColor)] truncate'>{unit}</div>
          </div> */}

          {isPOS && (
            <>
              {/* Attribute  */}
              {/* <div className='flex flex-col justify-between h-full gap-[11px] flex-shrink-0 w-[180px]'>
                <div className='text-[14px] text-[var(--textBlack56)]'>Thuộc tính</div>
                <div className='text-[14px] font-medium text-[var(--primaryColor)] truncate'>
                  {attribute}{' '}
                  <span className='text-[var(--sematicSuccessColor500)]'> +{formatPrice(atrtributePrice || 0)}</span>
                </div>
              </div> */}

              {/* POS quantity  */}
              <div className='flex flex-col justify-center h-full items-center gap-[11px] flex-shrink-0 w-[120px]'>
                <div className='text-[14px] text-[var(--textBlack56)]'>Số lượng</div>
                <div className='rounded-md text-center'>
                  <div className='text-[14px] font-medium text-[var(--primaryColor)]'>{quantity}</div>
                </div>
              </div>

              {/* Dimension  */}
              <div className='flex flex-col justify-between h-full gap-[12px] mt-1 flex-shrink-0 w-[120px]'>
                <div className='text-[14px] text-[var(--textBlack56)]'>Quy cách</div>
                <div className='flex items-center'>
                  <Tooltip
                    trigger={['hover', 'click']}
                    placement='bottomLeft'
                    overlayInnerStyle={{
                      backgroundColor: 'white',
                      color: '#666',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    }}
                    title={
                      <div style={{ maxWidth: 320, color: '#666' }}>
                        {Array.isArray(dimension) ? (
                          dimension.length > 0 ? (
                            <ul className='list-disc pl-4 m-0' style={{ color: '#666' }}>
                              {dimension.map((d, idx) => (
                                <li key={idx} style={{ marginBottom: idx < dimension.length - 1 ? '4px' : 0 }}>
                                  {d}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <span style={{ color: '#999' }}>Không có quy cách</span>
                          )
                        ) : dimension && (dimension as string).length > 0 ? (
                          dimension
                        ) : (
                          <span style={{ color: '#999' }}>Không có quy cách</span>
                        )}
                      </div>
                    }
                  >
                    <button
                      type='button'
                      className='ml-5  text-[var(--primaryColor500)] hover:opacity-80 transition-opacity'
                      aria-label='Xem chi tiết quy cách'
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Eye size={18} className='text-[#666]' weight='bold' />
                    </button>
                  </Tooltip>
                </div>
              </div>
            </>
          )}

          {/* Subtotal  */}
          {isDisplaySubtotal && (
            <div className='flex flex-col justify-between h-full gap-[11px] flex-shrink-0 w-[150px]'>
              <div className='text-[14px] text-[var(--textBlack56)]'>{isPOS ? 'Tổng' : 'Tạm tính'}</div>
              <div
                className={`text-[14px] font-bold ${
                  !isQuickView
                    ? difference === undefined
                      ? ''
                      : difference === 0
                        ? 'text-[var(--primaryColor500)]'
                        : difference > 0
                          ? 'line-through text-[var(--sematicWarningColor)]'
                          : difference < 0
                            ? 'line-through text-[var(--sematicErrorColor500)]'
                            : ''
                    : ''
                } ${isQuickView ? 'text-[var(--primaryColor500)]' : ''}`}
              >
                {formatCurrency(subtotal || 0)} {currencyUnit}
              </div>
            </div>
          )}

          {/* Total  */}
          {!isQuickView && isDisplayTotal && (
            <div className='flex flex-col justify-between h-full gap-[11px] flex-shrink-0 w-[150px]'>
              <div className='text-[14px] text-[var(--textBlack56)]'>Tổng tiền</div>
              <div className='text-[14px] font-bold text-[var(--primaryColor500)]'>
                {total !== undefined && total !== null ? `${formatCurrency(total)} ${currencyUnit}` : '---'}
              </div>
            </div>
          )}
        </div>
      </HorizontalScrollContainer>
    </div>
  );
};

export default ProductItem;
